import type { DefaultValidKeyCodesT } from '@mentimeter/hotkeys';
import { useGetControlContext } from '@mentimeter/question-modules-contexts';
import type { ControlActionT } from '@mentimeter/question-modules-types';
import * as React from 'react';
import {
  useDispatch,
  useHotkeyOverlay,
  usePace,
  useQfa,
  useQuestion,
  useTranslate,
} from '../../data-mediation';
import { usePresentationSettings } from '../../data-mediation/series/usePresentationSettings';
import { showMedia } from '../../misc/media-overlay/control';
import { useShowMedia } from '../../misc/media-overlay/state';
import renderLog from '../../utils/renderCount';
import { SlideControlsButton } from './SlideControlsButton';

interface PropsT {
  questionControls: ControlActionT[];
  displayControls?: boolean;
}

export const C = React.memo<PropsT>(
  ({ questionControls, displayControls }: PropsT) => {
    renderLog('Controls (Toolbar)');
    const question = useQuestion();
    const showHotkeyOverlay = useHotkeyOverlay((state) => state.show);
    const showEndStatus = usePace().state?.hasReachedEnd;
    if (showHotkeyOverlay || showEndStatus) {
      return null;
    }

    return (
      <>
        {questionControls.map((action) => (
          <SlideControlsButton
            key={action.id}
            slideId={question.id}
            action={action}
            displayControl={
              action.displayControl !== false && Boolean(displayControls)
            }
          />
        ))}
      </>
    );
  },
);

const Controls = () => {
  const dispatch = useDispatch();
  const { controls: moduleControls, extraControls: moduleExtraControls } =
    useGetControlContext();
  const { qaEnablementScope } = usePresentationSettings();
  const question = useQuestion();

  const translate = useTranslate();
  const showMediaState = useShowMedia();

  const questionControls = React.useMemo(() => {
    const { module_id: moduleId } = question;

    if (!moduleId) return [];

    const trackModuleAction = (placement: string, action: string) =>
      dispatch({
        type: 'TRACK_MODULE_ACTION',
        meta: {
          tracking: {
            placement,
            'question slide module': moduleId,
            action,
          },
        },
      });

    const extraControls = moduleExtraControls
      .map((control) =>
        control === 'showMedia' && question.cropped_image_url
          ? showMedia(showMediaState, translate)
          : false,
      )
      .filter((control): control is ControlActionT => control !== false);

    const controls = [...moduleControls, ...extraControls].map((c) => ({
      ...c,
      onClick: () => {
        trackModuleAction('Action button', c.trackingAction);
        c.onClick();
      },
      onHotkey: (keycode: DefaultValidKeyCodesT) => {
        trackModuleAction(
          `Hotkey ${(keycode || c.hotkey).toUpperCase()}`,
          c.trackingAction,
        );
        c.onHotkey?.(keycode);
      },
    }));

    return controls;
  }, [
    question,
    showMediaState,
    dispatch,
    translate,
    moduleExtraControls,
    moduleControls,
  ]);
  const hideVideoSlideControls =
    question.slide_type === 'video' && !question.question_video_embed_url;

  const { enabled: qfaEnabled } = useQfa();
  const isQfaEnabled = qfaEnabled && qaEnablementScope === 'all-slides';
  const displayControls = !hideVideoSlideControls && !isQfaEnabled;
  return (
    <C questionControls={questionControls} displayControls={displayControls} />
  );
};

export default Controls;
